import { User } from '@bonusly/who-am-i';

import { NavItemRequirements, NavLinkConfig, NavSectionConfig, NavSubsection } from '../types/nav';

/**
 * Checks if a user meets the requirements for a nav item to be visible
 */
export const meetsRequirements = (requirements: NavItemRequirements | undefined, user: User): boolean => {
	// If no requirements specified, item is always visible
	if (!requirements) {
		return true;
	}

	// Check role requirement (user must have at least ONE of the required roles)
	if (requirements.roles && requirements.roles.length > 0) {
		const hasRequiredRole = requirements.roles.some((role) => user[role]);
		if (!hasRequiredRole) {
			return false;
		}
	}

	// Check custom requirement boolean
	if (requirements.customCheck !== undefined && !requirements.customCheck) {
		return false;
	}

	return true;
};

/**
 * Filters an array of nav links based on user permissions and feature flags
 */
export const filterNavLinks = (links: NavLinkConfig[], user: User): NavLinkConfig[] => {
	return links.filter((link) => meetsRequirements(link.requirements, user));
};

/**
 * Filters a subsection's links based on user permissions and feature flags
 */
export function filterSubsection(subsection: NavSubsection, user: User): NavSubsection | null {
	// Check if the subsection itself meets requirements
	if (!meetsRequirements(subsection.requirements, user)) {
		return null;
	}

	// Filter the links within the subsection
	const filteredLinks = subsection.links.filter((link) => meetsRequirements(link.requirements, user));

	// Only return the subsection if it has visible links
	if (filteredLinks.length === 0) {
		return null;
	}

	return {
		...subsection,
		links: filteredLinks,
	};
}

/**
 * Filters nav sections and links and their items based on user permissions and feature flags.
 * Supports both direct links and nested subsections.
 */
export function filterNavSections(sections: NavSectionConfig[], user: User): NavSectionConfig[] {
	return sections
		.map((section) => {
			// A direct-link section (has `url`) has no children to filter; pass through.
			if (section.url) {
				return section;
			}

			// Filter direct links if they exist
			const filteredLinks = section.links ? section.links.filter((link) => meetsRequirements(link.requirements, user)) : [];

			// Filter subsections if they exist
			const filteredSubsections = section.subsections
				? section.subsections.map((subsection) => filterSubsection(subsection, user)).filter((s): s is NavSubsection => s !== null)
				: [];

			return {
				...section,
				links: filteredLinks.length > 0 ? filteredLinks : undefined,
				subsections: filteredSubsections.length > 0 ? filteredSubsections : undefined,
			};
		})
		.filter((section) => {
			// Keep a direct-link section, or a group section that still has visible
			// links or subsections.
			if (section.url) {
				return true;
			}
			const hasLinks = section.links && section.links.length > 0;
			const hasSubsections = section.subsections && section.subsections.length > 0;
			return hasLinks || hasSubsections;
		});
}
